import type {
  ConditionalString,
  FieldCondition,
  SchemaField,
  SubmitConfig,
  UserTypeSchema,
  UserTypeSelector,
} from '../types';

import i18n from 'i18n-react';

// Field types rendered as text-like inputs
export const TEXT_INPUT_TYPES = ['text', 'email', 'password'];

/**
 * Evaluates a schema condition against form data.
 * Returns true when no condition is provided (i.e. unconditional).
 */
export const evaluateCondition = (condition: FieldCondition | undefined, data: Record<string, unknown>): boolean => {
  if (!condition) return true;

  const { field, operator, value } = condition.when;
  const actual = typeof data[field] === 'string' ? data[field] : '';

  switch (operator) {
    case 'equals':
      return actual === value;
    case 'not_equals':
      return actual !== value;
    default:
      return true;
  }
};

/**
 * Resolves a conditional string value against form data.
 * If the value is a plain string, it is returned as-is.
 */
export const resolveConditionalValue = (value: string | ConditionalString, data: Record<string, unknown>): string => {
  if (typeof value === 'string') return value;
  return evaluateCondition(value, data) ? value.then : value.else;
};

/**
 * Validates all visible fields in the given schema against form data.
 * Returns an array of translated error messages (empty if all valid).
 */
export const validateSchemaFields = (schema: UserTypeSchema, data: Record<string, unknown>): string[] => {
  const errors: string[] = [];

  const checkField = (field: SchemaField) => {
    if (!evaluateCondition(field.visible, data)) return;

    const { validation } = field;
    if (!validation) return;

    const value = data[field.name];
    const strValue = typeof value === 'string' ? value : '';

    if (validation.minLength) {
      if (strValue.length < validation.minLength.value) {
        errors.push(i18n.translate(validation.minLength.message) as string);
        return; // One error per field
      }
    }

    if (validation.matchField) {
      const rawOther = data[validation.matchField.field];
      const otherValue = typeof rawOther === 'string' ? rawOther : '';
      if (strValue !== otherValue) {
        errors.push(i18n.translate(validation.matchField.message) as string);
        return;
      }
    }

    if (validation.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (strValue && !emailRegex.test(strValue)) {
        errors.push(i18n.translate(validation.email.message) as string);
      }
    }
  };

  for (const section of schema.sections) {
    for (const subsection of section.subsections) {
      for (const field of subsection.fields) {
        checkField(field);
      }
    }
  }

  return errors;
};

/**
 * Counts how many times each `name` appears in a user-type schema (duplicate names are skipped for auto-clear).
 */
const countFieldNamesBySchema = (schema: UserTypeSchema): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const section of schema.sections) {
    for (const subsection of section.subsections) {
      for (const field of subsection.fields) {
        counts.set(field.name, (counts.get(field.name) ?? 0) + 1);
      }
    }
  }
  return counts;
};

const fieldValueIsEmpty = (field: SchemaField, value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (field.type === 'checkbox') {
    return value !== true && value !== 'on' && value !== 'true';
  }
  return value === '';
};

/**
 * For fields with a `visible` rule that is currently false, returns updates that reset their stored value
 * (`''` for text-like/select, `false` for checkbox). Skips names that appear more than once in the schema
 * (e.g. two `reg_state` variants) so we do not clear shared state incorrectly.
 */
export const getClearedUpdatesForNonVisibleFields = (
  schema: UserTypeSchema,
  data: Record<string, unknown>,
): Record<string, string | boolean> => {
  const nameCounts = countFieldNamesBySchema(schema);
  const updates: Record<string, string | boolean> = {};
  const dataForEval: Record<string, unknown> = { ...data };

  for (const section of schema.sections) {
    for (const subsection of section.subsections) {
      for (const field of subsection.fields) {
        if (!field.visible) continue;
        // Same `name` can appear twice (e.g. US state select vs free-text `reg_state`); they share one form value, so do not auto-clear.
        if ((nameCounts.get(field.name) ?? 0) > 1) continue;
        if (evaluateCondition(field.visible, dataForEval)) continue;

        const emptyValue: string | boolean = field.type === 'checkbox' ? false : '';
        if (fieldValueIsEmpty(field, dataForEval[field.name])) continue;

        updates[field.name] = emptyValue;
        dataForEval[field.name] = emptyValue;
      }
    }
  }

  return updates;
};

/**
 * Builds an API payload from form data using the schema's submit config.
 * Maps form field names → API parameter names and applies transformations.
 */
export const buildApiPayload = (
  data: Record<string, unknown>,
  submitConfig: SubmitConfig,
  userTypeSelector: UserTypeSelector,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  // Include userTypeSelector fields (e.g. registrationCode)
  for (const field of userTypeSelector.fields) {
    result[field.apiKey] = data[field.name];
  }

  // Map form fields to API fields
  for (const [formField, apiField] of Object.entries(submitConfig.apiMapping)) {
    let value = data[formField];

    const transformation = submitConfig.transformations?.[formField];
    const transformationType = typeof transformation === 'string' ? transformation : transformation?.type;

    if (transformationType === 'boolean') {
      value = value === 'on' || value === true || value === 'true';
    } else if (transformationType === 'join') {
      const separator = typeof transformation === 'object' ? (transformation.separator ?? ' ') : ' ';
      const prefix = `${formField}_`;
      value = Object.entries(data)
        .filter(([key, val]) => key.startsWith(prefix) && Boolean(val))
        .map(([, val]) => val as string)
        .join(separator);
    }

    result[apiField] = value;
  }

  // Fallback to pre-filled login email
  if (!result.email && data.email) {
    result.email = data.email;
  }

  return result;
};
